import React, { useState, useEffect, useRef } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ActionButton from '../atoms/ActionButton';
import DatePicker from '../atoms/DatePicker';

const formSchema = z.object({
  objective: z
    .string()
    .min(1, 'タイトルを入力してください')
    .max(50, '50文字以内で入力してください'),
  style: z.string().min(1, 'スタイルを選択してください'),
  subStyle: z.string().min(1, 'サブスタイルを選択してください'),
});

export type AIGenerateFormData = z.infer<typeof formSchema>;

interface AIGenerateFormProps {
  defaultValues?: AIGenerateFormData;
  onSubmit?: SubmitHandler<AIGenerateFormData>;
}

const styleOptions = [
  { label: '基本', value: 'basic' },
  { label: 'ビジネス関連', value: 'business' },
  { label: '感情・用途別', value: 'emotion' },
  { label: '文体・特色別', value: 'style' },
  { label: '年代・対象別', value: 'target' },
];

const subStyleOptions = {
  basic: [
    { label: 'カジュアル', value: 'casual' },
    { label: 'フレンドリー', value: 'friendly' },
    { label: 'ビジネスフレンドリー', value: 'business-friendly' },
    { label: 'フォーマル', value: 'formal' },
  ],
  business: [
    { label: '上司向け', value: 'boss' },
    { label: '部下向け', value: 'subordinate' },
    { label: '同僚向け', value: 'colleague' },
    { label: '営業・セールス', value: 'sales' },
    { label: 'カスタマーサポート', value: 'customer-support' },
  ],
  emotion: [
    { label: '感謝', value: 'thanks' },
    { label: 'お祝い', value: 'congratulations' },
    { label: 'お詫び', value: 'apologies' },
    { label: '励まし', value: 'encouragement' },
  ],
  style: [
    { label: '簡潔', value: 'concise' },
    { label: '詳細説明', value: 'detailed' },
    { label: '関西弁', value: 'kansai' },
    { label: 'ユーモア', value: 'humor' },
  ],
  target: [
    { label: '若者向け', value: 'young' },
    { label: 'シニア向け', value: 'senior' },
    { label: '家族向け', value: 'family' },
  ],
};

const AIGenerateForm: React.FC<AIGenerateFormProps> = ({
  defaultValues,
  onSubmit = () => {},
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousStyleRef = useRef<string>('');

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    control,
    setValue,
    formState: { errors },
  } = useForm<AIGenerateFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const style = watch('style');
  const currentSubStyleOptions =
    subStyleOptions[style as keyof typeof subStyleOptions] || [];

  // styleが変更された時にsubStyleをリセット
  useEffect(() => {
    if (
      style &&
      previousStyleRef.current &&
      style !== previousStyleRef.current
    ) {
      setValue('subStyle', '');
    }
    previousStyleRef.current = style;
  }, [style, setValue]);

  const handleFormSubmit: SubmitHandler<AIGenerateFormData> = async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='p-ai p-form-box -ai'>
      <form onSubmit={handleSubmit(handleFormSubmit)} className='p-form -ai'>
        <h2 className='p-ai__title'>【 AI 生成 】</h2>
        <dl className='p-form__item'>
          <dt>
            <label htmlFor='from' className='p-form__label -small'>
              メッセージ本文のタイトル
              <br />
              （用途やシーン）
            </label>
          </dt>
          <dd>
            <input
              type='text'
              className='p-form__input'
              placeholder='お母さんの誕生日'
              {...register('objective')}
            />
            {errors.objective && (
              <p className='c-txt-error'>{errors.objective.message}</p>
            )}
          </dd>
        </dl>

        <dl className='p-form__item'>
          <dt>
            <label htmlFor='from' className='p-form__label'>
              スタイル
            </label>
          </dt>
          <dd>
            <div className='p-form__radio-group'>
              {styleOptions.map((option) => (
                <div className='p-form__radio-item' key={option.value}>
                  <input
                    type='radio'
                    id={option.value}
                    value={option.value}
                    {...register('style')}
                  />
                  <label htmlFor={option.value}>{option.label}</label>
                </div>
              ))}
            </div>
            {errors.style && (
              <p className='c-txt-error'>{errors.style.message}</p>
            )}
          </dd>
        </dl>
        {style && (
          <dl className='p-form__item'>
            <dt>
              <label htmlFor='from' className='p-form__label'>
                サブスタイル
              </label>
            </dt>
            <dd>
              <div className='p-form__radio-group'>
                {currentSubStyleOptions.map((option) => (
                  <div className='p-form__radio-item' key={option.value}>
                    <input
                      type='radio'
                      id={option.value}
                      value={option.value}
                      {...register('subStyle')}
                    />
                    <label htmlFor={option.value}>{option.label}</label>
                  </div>
                ))}
              </div>
              {errors.subStyle && (
                <p className='c-txt-error'>{errors.subStyle.message}</p>
              )}
            </dd>
          </dl>
        )}

        <div className='u-mt60'>
          <ActionButton type='submit' disabled={isSubmitting}>
            {isSubmitting ? '生成中...' : '文章生成'}
          </ActionButton>
        </div>
      </form>
    </div>
  );
};

export default AIGenerateForm;
